// src/lib/api/linkAgentApi.ts
//
// V2 onboarding chat — Nexilink-specific wrapper for the
// /api/link/agent/sessions/* + /api/link/finalize
// endpoints. Mirrors jasit-api-kit's AIAgentChatSessionApi shape but
// points at the Nexilink URLs. SSE streaming via fetch + ReadableStream
// (same pattern as the kit's src/sse.ts — inlined here so we don't need
// to bump the kit just to re-export one symbol).
//
// Use this for the V2 chat-driven module creation flow. Sessions are
// employer-scoped server-side (auth context resolves the employer's
// user ID); no scope param needed in requests.

import { getApiClient } from './client';

// ── Types ────────────────────────────────────────────────────────────

/** One selectable provider+model for the DEV model-picker. needs_key flags
 *  providers without a configured API key (shown disabled). */
export interface ModelOption {
    provider: string;
    id: string;
    label: string;
    needs_key: boolean;
}

/** Returned by start / get / resume / set-model. */
export interface LinkAgentSessionInfo {
    session_id: string;
    title?: string;
    model: string;
    provider: string;
    created_at: string;
    plan_mode: boolean;
    /** DEV model-picker: providers/models this session can switch to. */
    available_models?: ModelOption[];
    /** #298 — set ONLY by /resume: the LIVE in-memory conversation. Preferred
     *  over the persisted snapshot when it has more entries (a turn backgrounded
     *  mid-flight isn't in the snapshot yet). Same shape as employer-chat detail
     *  `messages`. */
    messages?: import('./employerChatApi').EmployerChatMessage[];
    /** #318 — `messages` above is WINDOWED to the last N exchanges so a long
     *  chat opens instantly instead of mounting hundreds of markdown blocks.
     *  These report the full size and whether older messages exist; fetch them
     *  with employerChatApi.get(id, 0). Link's own context is never windowed. */
    total_messages?: number;
    has_more?: boolean;
    /** #26 — set ONLY by /resume: the ORIGINAL suggest_next hint stored on the
     *  chat's last message, so a reopened chat re-shows the exact suggestion the
     *  user left on (not a regenerated one). Empty/absent when none was recorded. */
    suggestion?: string;
}

/** Body for /respond — verdicts on each pending approval. */
export interface LinkAgentRespondRequest {
    decisions: Array<{
        call_id: string;
        approve: boolean;
        deny_reason?: string;
        // Optional — lets the backend route "approve for this session"
        // semantics (pattern-2 tools like get_user_profile) without
        // a server-side lookup. Always send the tool_name from the
        // pending approval so the server can decide; the backend
        // ignores it when not relevant.
        tool_name?: string;
    }>;
}

/** Body for /answer — user's response to an ask_user pause. */
export interface LinkAgentAnswerRequest {
    answers: string[];
}

/** Body for /answer-text — a free-form REPLY instead of an answer (#111 B1).
 *  Distinct from typing a custom answer, which /answer already carries: that
 *  is still an answer and the model is told the user chose it. This says the
 *  question went UNANSWERED and here is what they said instead. */
export interface LinkAgentAnswerTextRequest {
    text: string;
}

/** Body for /answer-form — a multi-question form pause (#111 C). POSITIONAL:
 *  entry i answers question i, in the order asked. `note` is a caveat attached
 *  TO an answer, never an answer itself. */
export interface LinkAgentAnswerFormRequest {
    answers: Array<{ values?: string[]; note?: string }>;
}

/** Artifact shape selected at chat-session create — markdown is the
 *  V2.0 default (parser-driven save); json is the V2.1 variant
 *  (zero-translation save, structured artifact). Persists per session;
 *  can't be changed mid-session. */
export type ArtifactShape = 'markdown' | 'json';

/** Body for /finalize — the artifact content the AI emitted + the shape
 *  it was emitted in + optional source IDs override + the artefact
 *  filename which the backend uses to route between module-finalize
 *  (default / `module-*.md`) and need-finalize (`annons-*.md`,
 *  Phase 3). */
export interface OnboardingFinalizeRequest {
    shape: ArtifactShape;
    content: string;
    filename?: string;
    /** The framework's stable artifact UUID (activeArtifact.id). The
     *  backend looks it up in chat_artifact_refs to decide whether
     *  this save targets an existing Need/Module (UPDATE) or creates
     *  a new one. Pass it on EVERY finalize call — omitting it falls
     *  back to create-only semantics and produces a duplicate row on
     *  the second save of the same artefact. */
    artifact_id?: string;
    source_ids?: string[];
    /** The chat this artefact was built in. Used for ONE thing on the backend:
     *  folding the chat's history once the save lands (R10,
     *  LINK_COMPACT_ON_SAVE). Omitting it just means no post-save compaction —
     *  the save itself never depends on it. */
    session_id?: string;
}

/** Response from /finalize. Discriminated union by `kind`:
 *  - "module" → module fields (V2.0 default path)
 *  - "need"   → need / job-posting fields (Phase 3 annons path)
 *  Old payloads without `kind` are treated as modules for
 *  backward compatibility. */
export type OnboardingFinalizeResponse =
    | {
          kind?: 'module';
          module_id: string;
          module_name: string;
          module_description: string;
          estimated_duration?: string;
      }
    | {
          kind: 'need';
          need_id: string;
          title: string;
          role: string;
          city: string;
          status: string;
          start_date: string;
      };

/** Discriminated union of every SSE event the V2 chat endpoints emit.
 *  Mirrors the framework's chat.Event kinds (chat/event.go). */
export type LinkAgentStreamEvent =
    | { event: 'started'; data: { session_id: string } }
    | { event: 'text'; data: { text: string } }
    | {
          event: 'tool_start';
          data: { name: string; args?: unknown; description?: string };
      }
    | {
          event: 'tool_end';
          // result_label is the framework's tools.ResultDescriber output
          // (go.jasit.base v1.8.0 / Proposal 12) — a short summary of
          // what came back, e.g. "1.2 kB lästa". Optional; absent when
          // the tool doesn't implement the interface.
          data: { name: string; result?: unknown; result_label?: string };
      }
    | {
          event: 'artifact';
          data: { id?: string; name: string; mime: string; content_b64: string; size: number };
      }
    | {
          // §6 (Multi Artifact Flow, go.jasit.base v1.11.0) — fires when
          // the model calls delete_artifact. Bytes are gone; the event
          // only carries id + name so frontends can drop the artifact
          // from the preview / list view. Nexilink V2 is single-artifact
          // today — this is defensive plumbing for when the model
          // multi-emits and curates its own drafts.
          event: 'artifact_deleted';
          data: { id: string; name: string };
      }
    | {
          event: 'approval_needed';
          // Wire shape — snake_case, matching the rest of the framework
          // HTTP surface, since framework v1.3.0 added json tags to
          // ai.ApprovalRequest and chat.Question.
          data: {
              approvals: Array<{ call_id: string; tool_name: string; args: unknown }>;
              hint: string;
          };
      }
    | {
          event: 'ask_user';
          data: {
              question: { prompt: string; options: string[]; multi: boolean };
              hint: string;
          };
      }
    | { event: 'compacted'; data: { summary: string } }
    | {
          // #288 Layer 2 — the FIRST frame of a /reattach-stream response: tells
          // the client whether there's a live turn (`running`) and where the
          // replay sits (base/next offset) so it can track a cursor and decide
          // gap-handling. Transport-internal — mapNexilinkEvent consumes it and
          // never forwards it to the kit reducer. running:false ⇒ no live turn,
          // fall back to resume-from-history.
          event: 'reattach';
          data: { running: boolean; base_offset?: number; next_offset?: number; gap?: boolean };
      }
    | {
          event: 'done';
          data: {
              usage?: {
                  prompt_tokens?: number;
                  completion_tokens?: number;
                  total_tokens?: number;
              };
          };
      }
    | {
          // §8.18 — framework's Session.SuggestNext fires once after
          // each stream ends. Emitted only when the session opted in
          // (EnableSuggestNext) AND the cheap-model generator
          // returned a non-empty text. Frontend renders as a
          // Tab-to-send hint below the textarea.
          event: 'suggest_next';
          data: { text: string };
      }
    | { event: 'error'; data: { error: string } };

// ── SSE helper (inlined; same shape as jasit-api-kit's postSSE) ──────

interface RawSSEEvent {
    event: string;
    data: string;
}

async function* postSSE(
    path: string,
    body: unknown,
    signal?: AbortSignal,
): AsyncGenerator<RawSSEEvent> {
    const client = getApiClient();
    const url = client.getBaseURL().replace(/\/$/, '') + path;

    const doFetch = async (): Promise<Response> => {
        const token = client.getAccessToken();
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        return fetch(url, {
            method: 'POST',
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
            signal,
        });
    };

    let response = await doFetch();

    // 401 recovery — long-running chat sessions (paused turns waiting
    // for the user) routinely outlive the access token's TTL. The kit's
    // regular axios calls handle this via a 401 interceptor; SSE bypasses
    // axios so we have to refresh + retry here. Same shape as the fix
    // shipped in jasit-api-kit v0.6.2.
    if (response.status === 401) {
        try {
            await client.refreshTokens();
            response = await doFetch();
        } catch {
            // refresh failed — let the original 401 surface below
        }
    }

    if (!response.ok) {
        let errBody = '';
        try {
            errBody = await response.text();
        } catch {
            // ignore
        }
        const suffix = errBody ? `: ${errBody}` : '';
        throw new Error(`SSE request failed: ${response.status} ${response.statusText}${suffix}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('SSE response has no body to read');

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = 'message';
    let dataBuffer: string | null = null;

    // Spec-compliant SSE parser: `field:value` or `field: value` (space
    // after colon is optional and stripped). A blank line dispatches.
    // Gin's sse encoder emits `event:foo\ndata:{...}\n\n` (no spaces),
    // so any parser that hardcodes the space silently drops every event.
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const rawLine of lines) {
                const line = rawLine.replace(/\r$/, '');
                if (line === '') {
                    // Blank line = event terminator. Dispatch if buffered.
                    if (dataBuffer !== null) {
                        yield { event: currentEvent, data: dataBuffer };
                        dataBuffer = null;
                        currentEvent = 'message';
                    }
                    continue;
                }
                if (line.startsWith(':')) continue; // comment line per spec
                const colonIdx = line.indexOf(':');
                const field = colonIdx < 0 ? line : line.slice(0, colonIdx);
                let value = colonIdx < 0 ? '' : line.slice(colonIdx + 1);
                if (value.startsWith(' ')) value = value.slice(1);
                switch (field) {
                    case 'event':
                        currentEvent = value;
                        break;
                    case 'data':
                        dataBuffer = dataBuffer === null ? value : dataBuffer + '\n' + value;
                        break;
                    // id / retry — ignored; this framework doesn't use them
                }
            }
        }
        // Stream closed mid-event — dispatch what we have.
        if (dataBuffer !== null) {
            yield { event: currentEvent, data: dataBuffer };
        }
    } finally {
        reader.releaseLock();
    }
}

// iterateEvents parses each raw SSE event's JSON payload and yields a
// typed event. Malformed payloads bubble up as synthetic error events
// so the caller's switch handles them uniformly.
async function* iterateEvents(
    raw: AsyncGenerator<RawSSEEvent>,
): AsyncGenerator<LinkAgentStreamEvent> {
    for await (const { event, data } of raw) {
        if (event === 'message') continue; // synthetic SSE default — skip
        let parsed: unknown;
        try {
            parsed = data.length > 0 ? JSON.parse(data) : {};
        } catch {
            yield { event: 'error', data: { error: `failed to parse SSE data: ${data}` } };
            continue;
        }
        yield { event, data: parsed } as LinkAgentStreamEvent;
    }
}

// ── Public API ───────────────────────────────────────────────────────

/** Start a new onboarding chat session for the authenticated employer.
 *  Shape defaults to markdown server-side when omitted. provider/model are
 *  optional DEV overrides (the dev model-picker) — omit for the pinned default. */
const start = async (
    title?: string,
    shape?: ArtifactShape,
    provider?: string,
    model?: string,
): Promise<LinkAgentSessionInfo> => {
    return await getApiClient().post<LinkAgentSessionInfo>(
        '/api/link/agent/sessions',
        { title, shape, provider, model },
    );
};

/** DEV model-picker: switch the live session's provider/model mid-chat. The
 *  engine applies the switch between turns; subsequent messages use the new
 *  model. Returns the updated session info (with the new current model). */
const setModel = async (
    sessionId: string,
    provider: string,
    model?: string,
): Promise<LinkAgentSessionInfo> => {
    return await getApiClient().post<LinkAgentSessionInfo>(
        `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/model`,
        { provider, model },
    );
};

/** Resume a persisted chat by ID. Server re-creates the in-process
 *  chat.Session with the saved messages preloaded. The returned info
 *  has the SAME session_id as the original chat. */
const resume = async (chatId: string): Promise<LinkAgentSessionInfo> => {
    return await getApiClient().post<LinkAgentSessionInfo>(
        `/api/link/agent/sessions/${encodeURIComponent(chatId)}/resume`,
        {},
    );
};

/** Fetch session info (lightweight — no message history). */
const get = async (sessionId: string): Promise<LinkAgentSessionInfo> => {
    return await getApiClient().get<LinkAgentSessionInfo>(
        `/api/link/agent/sessions/${encodeURIComponent(sessionId)}`,
    );
};

/** Delete a session (cleanup; not strictly required — sessions die
 *  on server restart anyway in V2.0). */
const remove = async (sessionId: string): Promise<void> => {
    await getApiClient().delete(`/api/link/agent/sessions/${encodeURIComponent(sessionId)}`);
};

/** Cancel the in-flight turn on a session (Stop button). Aborts the
 *  framework turn server-side and releases the in-flight lock so the
 *  next message can be sent. Idempotent — safe to call when nothing is
 *  running. Best-effort: the caller also aborts the local SSE fetch for
 *  an immediate UI stop. */
const cancel = async (sessionId: string): Promise<void> => {
    await getApiClient().post(`/api/link/agent/sessions/${encodeURIComponent(sessionId)}/cancel`, {});
};

/** #288 — background the in-flight turn: unlike cancel, it does NOT stop the
 *  turn; the server keeps running it to completion after the chat window closes.
 *  Idempotent, no-op if no turn is live. */
const detach = async (sessionId: string): Promise<void> => {
    await getApiClient().post(`/api/link/agent/sessions/${encodeURIComponent(sessionId)}/detach`, {});
};

/** Status of a session's most recent turn (#288). Poll after detach to know
 *  when a backgrounded run has finished. `running` is true while in flight;
 *  when done, `state` is "done" or "failed" ("gone" if the session was wiped
 *  by a server restart). */
/** #288 — what a parked ("waiting") turn needs from the user, for a
 *  "Link needs you" cue. */
export interface TurnWaitingInfo {
    kind: 'approval' | 'ask_user' | 'continuation';
    summary?: string;
    tools?: string[];
}

export interface TurnStatus {
    running: boolean;
    state: 'idle' | 'running' | 'waiting' | 'done' | 'failed' | 'gone';
    detached?: boolean;
    waiting?: TurnWaitingInfo;
    finished_at?: string;
    error?: string;
}

const getTurnStatus = async (sessionId: string): Promise<TurnStatus> => {
    return await getApiClient().get<TurnStatus>(
        `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/status`,
    );
};

/** #298 — the pending pause re-presented on re-attach. Shapes mirror the SSE
 *  `approval_needed` / `ask_user` event data exactly, so the caller can drive
 *  the same pause UI and resume via /respond or /answer. */
export type ReattachPending =
    | { kind: 'approval'; approvals: Array<{ call_id: string; tool_name: string; args?: unknown }> }
    | { kind: 'ask_user'; question: { prompt: string; options: string[]; multi: boolean } };

export interface ReattachResult {
    reattached: boolean;
    pending?: ReattachPending;
}

/** #298 — live re-attach: when Link is reopened on a run that backgrounded
 *  WHILE paused (awaiting an approval / ask_user), this un-detaches the turn
 *  server-side and hands back the exact pending pause so the UI can re-present
 *  it and resume via the existing /respond or /answer. `reattached: false` means
 *  there's no live paused turn — fall back to plain resume-from-history. */
const reattach = async (sessionId: string): Promise<ReattachResult> => {
    return await getApiClient().post<ReattachResult>(
        `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/reattach`,
        {},
    );
};

/** One artifact currently alive in a persisted chat — wire shape
 *  mirrors the live `artifact` SSE event so the modal can feed it
 *  into the same tab-assembly path on resume. content_b64 is the
 *  artifact's raw bytes, base64-encoded. */
export interface LiveArtifact {
    id: string;
    name: string;
    mime: string;
    content_b64: string;
}

/** Fetch artifacts the chat session emitted (and didn't subsequently
 *  delete) so the preview pane can be repopulated on resume. Backend
 *  walks the persisted messages — no separate state to keep in sync.
 *  Returns an empty array (not 404) when the chat has none. */
const liveArtifacts = async (chatId: string): Promise<{ artifacts: LiveArtifact[] }> => {
    return await getApiClient().get<{ artifacts: LiveArtifact[] }>(
        `/api/link/agent/sessions/${encodeURIComponent(chatId)}/live-artifacts`,
    );
};

/** Silently push the user's manual markdown edits back into the chat
 *  session's ArtifactStore so Link's next get_artifact returns the
 *  fresh content. Used by the "Spara ändringar" handler — replaces
 *  the prior chat-message-dump mechanism (which posted the whole
 *  markdown into the chat as a user message asking Link to call
 *  update_artifact, polluting the transcript). Backend verifies
 *  artifactId belongs to this chat. 204 on success. */
const updateArtifact = async (
    sessionId: string,
    artifactId: string,
    content: string,
): Promise<void> => {
    await getApiClient().put(
        `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/artifacts/${encodeURIComponent(artifactId)}`,
        { content },
    );
};

/** One file the user attached to a message. ContentB64 is the file's
 *  raw bytes encoded as standard base64 (no `data:` URI prefix).
 *  Framework enforces a default policy (10 MB per file, 25 MB per
 *  turn, 5 files max); policy violations come back as HTTP 400. */
export interface LinkAgentAttachment {
    name: string;
    mime: string;
    content_b64: string;
}

/** One structured @-mention of a domain entity (#240 Wave 5). The backend
 *  resolves {type,id} into a context block prepended to the message text —
 *  the internal id never surfaces in the model or transcript. */
export interface LinkAgentReference {
    type: 'need' | 'module' | 'employee' | 'application'; // #47b — application injects need + candidate + evaluation
    id: string;
}

/** Send a user message; yields each SSE event as it arrives.
 *  Attachments are optional — when present, the AI reads them on
 *  this turn (PDF/DOCX/image/markdown supported by the framework's
 *  converter registry). References are optional structured @-mentions. */
async function* sendMessage(
    sessionId: string,
    text: string,
    attachments?: LinkAgentAttachment[],
    signal?: AbortSignal,
    references?: LinkAgentReference[],
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/messages`;
    const body: { text: string; attachments?: LinkAgentAttachment[]; references?: LinkAgentReference[] } = { text };
    if (attachments && attachments.length > 0) {
        body.attachments = attachments;
    }
    if (references && references.length > 0) {
        body.references = references;
    }
    yield* iterateEvents(postSSE(path, body, signal));
}

/** Resume a turn paused on approval_needed (incl. plan_ready). */
async function* respond(
    sessionId: string,
    req: LinkAgentRespondRequest,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/respond`;
    yield* iterateEvents(postSSE(path, req, signal));
}

/** Resume a turn paused on ask_user. */
async function* answer(
    sessionId: string,
    req: LinkAgentAnswerRequest,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/answer`;
    yield* iterateEvents(postSSE(path, req, signal));
}

/** Resolve a pending ask_user with a free-form reply (#111 B1). */
async function* answerText(
    sessionId: string,
    req: LinkAgentAnswerTextRequest,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/answer-text`;
    yield* iterateEvents(postSSE(path, req, signal));
}

/** Resolve a multi-question form pause (#111 C). */
async function* answerForm(
    sessionId: string,
    req: LinkAgentAnswerFormRequest,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/answer-form`;
    yield* iterateEvents(postSSE(path, req, signal));
}

/** #288 Layer 2 — reattach to a RUNNING backgrounded turn and STREAM the events
 *  missed while the page was gone, then continue the live turn. Unlike `reattach`
 *  (JSON, paused-only), this is SSE: the first frame is a `reattach` meta (running
 *  + offsets + gap), followed by the replayed partial turn (text/tool/etc), then
 *  the live tail to `done` (or a pause re-presented as approval_needed/ask_user).
 *  `fromOffset` is the absolute event cursor — 0 on a fresh reload. A meta with
 *  running:false means there's no live turn (resume from history instead). */
async function* reattachStream(
    sessionId: string,
    fromOffset = 0,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const q = fromOffset > 0 ? `?offset=${fromOffset}` : '';
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/reattach-stream${q}`;
    yield* iterateEvents(postSSE(path, {}, signal));
}

/** #300 — user-triggered non-destructive compaction ("Komprimera samtalet").
 *  Streams the framework's compaction events; the `compacted` event renders the
 *  same summary divider as auto-compaction. */
async function* compact(
    sessionId: string,
    signal?: AbortSignal,
): AsyncGenerator<LinkAgentStreamEvent> {
    const path = `/api/link/agent/sessions/${encodeURIComponent(sessionId)}/compact`;
    yield* iterateEvents(postSSE(path, {}, signal));
}

/** Finalize — parse the artifact markdown into a real Module + steps. */
const finalize = async (req: OnboardingFinalizeRequest): Promise<OnboardingFinalizeResponse> => {
    return await getApiClient().post<OnboardingFinalizeResponse>(
        '/api/link/finalize',
        req,
    );
};

/** Parsed module preview — what the backend's parser/decoder produces
 *  from the raw artifact. Same shape as ParsedLinkModule on the Go side.
 *  Used by the live view toggle so we can render the SAME content as
 *  module cards / markdown / JSON without restarting the session. */
export interface ParsedModulePreview {
    module_name: string;
    module_description: string;
    estimated_duration?: string;
    source_ids: string[];
    steps: Array<{
        step_order: number;
        step_type: string;
        step_title: string;
        content: any;
    }>;
}

/** Preview — parse the artifact without persisting. Lets the frontend
 *  render the same content in multiple views. */
const preview = async (shape: ArtifactShape, content: string): Promise<ParsedModulePreview> => {
    return await getApiClient().post<ParsedModulePreview>(
        '/api/link/preview',
        { shape, content },
    );
};

/** AI-generated welcome — a short greeting + 3-4 starter-prompt
 *  suggestions for the V2 chat modal's empty state. Cached server-side
 *  by source-set fingerprint; repeated opens are free until the user's
 *  library changes. Throws on any non-2xx — caller catches and falls
 *  back to the hardcoded static WelcomeCard. */
export interface WelcomeSuggestion {
    emoji: string;
    label: string;
    prompt: string;
}
export interface Welcome {
    greeting: string;
    suggestions: WelcomeSuggestion[];
}
const generateWelcome = async (): Promise<Welcome> => {
    return await getApiClient().post<Welcome>('/api/link/welcome', {});
};

/**
 * #48 — is this error the "session was wiped by a server restart" case?
 *
 * Link agent sessions are in-process/in-memory. On a backend restart (every
 * GoLand rerun in dev; any redeploy/crash in prod) the live session is gone, but
 * the chat TRANSCRIPT is persisted — so this is recoverable via `resume(chatId)`.
 *
 * The backend's session manager returns `session "<id>" not found (server restart
 * wipes in-process sessions)`, wrapped as an AI_INVALID_REQUEST (HTTP 400) by
 * every endpoint that first asserts ownership (status / messages / respond /
 * answer / reattach). That marker string reaches the client two ways:
 *   • SSE calls (sendMessage/respond/answer) throw a plain Error whose message is
 *     `SSE request failed: 400 Bad Request: <json body>` — the body carries it.
 *   • axios calls (getTurnStatus) throw the kit's ApiError — `.message` and
 *     `.details.reason` carry it, `.code` is AI_INVALID_REQUEST.
 *
 * We key on that unique marker (with an AI_INVALID_REQUEST + "not found" fallback)
 * so a REAL network error (retryable) is never mistaken for a wiped session
 * (which must stop the retry loop and recover instead).
 */
/** True when the request failed because the caller is no longer authenticated.
 *
 *  #308 — this is TERMINAL for a background poller, not transient. The api
 *  client already tries a token refresh on a 401 and only surfaces the error
 *  (after clearing the tokens and firing onSessionExpired) when that refresh
 *  FAILS. So a 401 reaching a caller means the session is genuinely dead and no
 *  amount of retrying can bring it back — polling on is a guaranteed-failing
 *  request every few seconds, each one firing another "Session Expired" toast. */
export function isAuthExpiredError(err: unknown): boolean {
    if (!err) return false;
    return (err as { status?: unknown }).status === 401;
}

export function isSessionGoneError(err: unknown): boolean {
    if (!err) return false;
    const anyErr = err as { message?: unknown; code?: unknown; status?: unknown; details?: { reason?: unknown } };
    // Framework Proposal 34: the backend now maps a wiped session to the STABLE
    // AI_SESSION_GONE code / HTTP 410 (chat.ErrSessionGone → NewAISessionGoneError),
    // so prefer structural detection over prose. Status 410 is the gone contract.
    if (anyErr.status === 410) return true;
    const parts: string[] = [];
    if (typeof anyErr.message === 'string') parts.push(anyErr.message);
    if (typeof anyErr.code === 'string') parts.push(anyErr.code);
    if (typeof anyErr.details?.reason === 'string') parts.push(anyErr.details.reason);
    const hay = parts.join(' ').toLowerCase();
    if (!hay) return false;
    // Primary: the stable code, present in the SSE error body AND the axios ApiError.
    if (hay.includes('ai_session_gone')) return true;
    // Legacy fallback (pre-Proposal-34 servers): the in-process manager's marker
    // prose, wrapped as AI_INVALID_REQUEST.
    if (hay.includes('server restart wipes')) return true;
    return hay.includes('ai_invalid_request') && hay.includes('not found');
}

/** DEV ONLY (#300/#317/#318) — create a Link chat whose transcript is ALREADY
 *  ~120k tokens, with no model calls made, so auto-compaction and the windowed
 *  history can be reached in one click instead of an afternoon of typing.
 *
 *  The route only exists in a dev build (APP_ENV=dev) and only ever seeds a chat
 *  for the calling user. `first_code`/`last_code` are the fixture's checkable
 *  markers: after a compaction the LAST one must come back verbatim (it sits in
 *  the kept recent turns) while an early one may not (it was summarised). */
export interface DevSeededChat {
    id: string;
    title: string;
    messages: number;
    turns: number;
    estimated_tokens: number;
    first_code: string;
    last_code: string;
}

const devSeedChat = async (tokens?: number): Promise<DevSeededChat> => {
    const q = tokens === undefined ? '' : `?tokens=${tokens}`;
    return getApiClient().post<DevSeededChat>(`/api/dev/link/seed-chat${q}`, {});
};

export const linkAgentApi = {
    devSeedChat,
    start,
    setModel,
    resume,
    get,
    remove,
    cancel,
    detach,
    getTurnStatus,
    reattach,
    reattachStream,
    liveArtifacts,
    updateArtifact,
    sendMessage,
    respond,
    answer,
    answerText,
    answerForm,
    compact,
    finalize,
    preview,
    generateWelcome,
};
